/**
 * Fragment Operations — Action definitions for fragment-based drug design
 *
 * Covers loading/unloading fragment libraries, fragment grow searches,
 * group replacement, and batch design operations.
 *
 * @module gibbs/registry/actions/fragment-ops
 */

import { WorkflowTypes as T } from '../types';
import { param, pre, post } from '../schema';

// ─── Load / Unload ─────────────────────────────────────────────────────────

export const loadFragmentMap = {
    id: 'load-fragment-map',
    name: 'Load Fragment Library',
    category: 'Fragment Operations',
    description: 'Load a complete fragment map (Boltzmann Map) for a protein case. Fragment maps contain pre-computed binding poses, energies, and statistics for thousands of fragments across the protein surface.',
    inputs: [
        param('fragmentInfo', 'object', 'Fragment library identifier {name, series_list}'),
    ],
    outputs: [
        param('fragmentMap', T.FragmentMap, 'Loaded fragment structures with binding poses'),
    ],
    parameters: [],
    dependsOn: ['load-protein-from-map'],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [
        post('fragment_map_available', 'Fragment map is loaded and available'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'GetFragmentMap',
    serverCommand: 'get-fragment-map',
    jobType: null,
};

export const refreshAvailableFragments = {
    id: 'refresh-available-fragments',
    name: 'Refresh Available Fragments',
    category: 'Fragment Operations',
    description: 'Reload the list of available fragment series for a protein case, optionally restoring previously visible fragments.',
    inputs: [
        param('mapCase', T.CaseData, 'Target protein case'),
    ],
    outputs: [
        param('availableFragments', `${T.Fragment }[]`, 'Updated list of available fragment series'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
    ],
    postconditions: [
        post('fragment_data_available', 'Fragment series list is up to date'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'RefreshAvailableFragments',
    serverCommand: 'get-available-fragments',
    jobType: null,
};

export const loadFragments = {
    id: 'load-fragments',
    name: 'Load Fragment Structures',
    category: 'Fragment Operations',
    description: 'Load specific fragment structures from the fragment library into the workspace for visualization and analysis.',
    inputs: [
        param('fragmentList', `${T.Fragment }[]`, 'Fragment identifiers to load'),
    ],
    outputs: [
        param('fragments', `${T.Fragment }[]`, 'Loaded fragment objects with 3D structures'),
    ],
    parameters: [],
    dependsOn: ['refresh-available-fragments'],
    preconditions: [
        pre('fragment_data_available', 'Fragment library must be available'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'LoadFragments',
    serverCommand: 'load-fragments',
    jobType: null,
};

// ─── Grow / Search ─────────────────────────────────────────────────────────

export const growFromAtom = {
    id: 'grow-from-atom',
    name: 'Fragment Grow Search',
    category: 'Fragment Operations',
    description: 'DIRECTIONAL fragment search: find fragments that could extend a compound along a specific BOND VECTOR (two atoms defining the growth direction). Returns ranked suggestions. This is for targeted growing in one direction. For proximity-based search (all directions around an atom), use search-near. For batch growing from every atom, use grow-from-all-atoms.',
    inputs: [
        param('bondVectorPair', T.BondVector, 'Two atoms defining the growth direction [from, to]'),
        param('findType', 'string', 'Search type', true, { options: ['Bond'] }),
    ],
    outputs: [
        param('suggestions', T.FragmentSearchResult, 'Ranked fragment suggestions with poses and scores'),
    ],
    parameters: [
        param('growMethodology', 'string', 'Fragment grow methodology selected in Workflow Checker', false, {
            defaultValue: 'fragment-best-pose',
            options: ['fragment-best-pose', 'fragment-all-poses'],
            optionLabels: {
                'fragment-best-pose': 'Grow with Fragment Best Pose',
                'fragment-all-poses': 'Grow with Fragment All Poses',
            },
        }),
        param('searchTolerance', 'number', 'Angular/distance tolerance for geometric matching', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('fragment_map_available', 'Fragment map must be available'),
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'GrowFromAtom',
    serverCommand: 'find-modifications',
    jobType: null,
};

export const searchNear = {
    id: 'search-near',
    name: 'Search Nearby Fragments',
    category: 'Fragment Operations',
    description: 'PROXIMITY fragment search: find fragments that could bind NEAR a specified atom (all directions, within a radius). Used for hotspot discovery. This is NON-DIRECTIONAL — it searches around the atom in every direction. For directional growing along a specific bond, use grow-from-atom.',
    inputs: [
        param('atom', 'Atom', 'Reference atom for proximity search'),
    ],
    outputs: [
        param('suggestions', T.FragmentSearchResult, 'Nearby fragment suggestions with poses'),
    ],
    parameters: [
        param('scope', 'string', 'Search quality/speed tradeoff', false, { defaultValue: 'best', options: ['best', 'fast'] }),
        param('searchRadius', 'number', 'Distance cutoff in Angstroms', false),
        param('searchTolerance', 'number', 'Matching tolerance', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('fragment_map_available', 'Fragment map must be available'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'SearchNear',
    serverCommand: 'find-modifications',
    jobType: null,
};

export const confirmModification = {
    id: 'confirm-modification',
    name: 'Accept Fragment Modification',
    category: 'Fragment Operations',
    description: 'Accept a fragment suggestion from grow or search results and create a new compound by incorporating the fragment. Handles both DataService-origin and BFD-Server-origin fragments.',
    inputs: [
        param('suggestion', T.FragmentSearchResult, 'The selected fragment suggestion'),
        param('sourceAtom', 'Atom', 'Reference atom for the modification'),
        param('pose', 'object', 'Specific pose to use (defaults to first)', false),
    ],
    outputs: [
        param('compound', T.Compound, 'New compound with fragment incorporated'),
    ],
    parameters: [],
    dependsOn: ['grow-from-atom', 'search-near'],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'ConfirmModification',
    serverCommand: 'select-modification',
    jobType: null,
};

// ─── Replace ───────────────────────────────────────────────────────────────

export const replaceGroup = {
    id: 'replace-group',
    name: 'Replace Functional Group',
    category: 'Fragment Operations',
    description: 'Swap a functional group on a compound with a different group. Used for R-group exploration and SAR studies.',
    inputs: [
        param('atom', 'Atom', 'Atom at the replacement point'),
        param('replacementGroup', 'string', 'Name of the replacement group (e.g., "methyl", "chloro", "amino")'),
    ],
    outputs: [
        param('compound', T.Compound, 'Modified compound with heritage tracked as terminal replacement'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'ReplaceGroup',
    serverCommand: 'replace-group',
    jobType: null,
};

export const growToFragmentName = {
    id: 'grow-to-fragment-name',
    name: 'Grow to Specific Fragment',
    category: 'Fragment Operations',
    description: 'Shortcut that combines fragment grow search with automatic selection of a named fragment. Searches from bond vector, finds the named fragment in results, and confirms the modification.',
    inputs: [
        param('bondVectorPair', T.BondVector, 'Growth direction'),
        param('findType', 'string', 'Search type', true, { options: ['Bond'] }),
        param('fragName', 'string', 'Target fragment name to select'),
    ],
    outputs: [
        param('compound', T.Compound, 'New compound with named fragment incorporated'),
    ],
    parameters: [
        param('scope', 'string', 'Search quality/speed tradeoff', false, { defaultValue: 'best', options: ['best', 'fast'] }),
    ],
    dependsOn: [],
    preconditions: [
        pre('fragment_map_available', 'Fragment map must be available'),
        pre('compound_loaded', 'A compound with growth vectors must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'GrowToFragmentName',
    serverCommand: null,
    jobType: null,
};

// ─── Advanced Queries ──────────────────────────────────────────────────────

export const fragDataQuery = {
    id: 'frag-data-query',
    name: 'Fragment Database Query',
    category: 'Fragment Operations',
    description: 'Advanced query against the fragment database with spatial, structural, and property filters. Supports geometric (find-near, find-bond) and metadata (fragment name, B-value, fragment set) filtering.',
    inputs: [
        param('userQuery', 'object', 'Complex query specification'),
    ],
    outputs: [
        param('results', T.FragmentSearchResult, 'Matching fragments or raw query results'),
    ],
    parameters: [
        param('findNearAtom', 'Atom', 'Center point for spatial search', false),
        param('findBondAtoms', T.BondVector, 'Directional vector search', false),
        param('mapCases', 'object[]', 'Restrict to specific protein cases', false),
        param('searchRadius', 'number', 'Spatial radius in Angstroms', false),
        param('maxBValue', 'number', 'B-factor cutoff', false),
        param('fragsets', 'string[]', 'Fragment sets to include', false),
        param('fragmentNames', 'string[]', 'Specific fragments to find', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('fragment_data_available', 'Fragment data must be available (BMapsDS running)'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'FragDataQuery',
    serverCommand: 'frag-data-query',
    jobType: null,
};

// ─── Batch Design Operations ───────────────────────────────────────────────

export const replaceAllGroups = {
    id: 'replace-all-groups',
    name: 'Exhaustive R-Group Replacement',
    category: 'Fragment Operations',
    description: 'Batch operation: replace all eligible atoms on one or more compounds with all specified replacement groups. Generates a combinatorial set of modified compounds for SAR exploration.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds to modify'),
    ],
    outputs: [
        param('results', 'object[]', 'Array of {compound, atom, replacement, newCompound, errors} for each combination'),
    ],
    parameters: [
        param('atomFilterFn', 'function', 'Filter for which atoms can be replaced', false),
        param('replacements', 'object[]', 'Group objects with .name property (default: first 8 terminal groups)', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'At least one compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'long',
    status: 'implemented',
    userAction: 'ReplaceAllGroups',
    serverCommand: null,
    jobType: null,
};

export const growFromAllAtoms = {
    id: 'grow-from-all-atoms',
    name: 'Exhaustive Fragment Grow',
    category: 'Fragment Operations',
    description: 'BATCH fragment growing (hit discovery): systematically try growing from ALL eligible bond vectors on one or more compounds. Produces many NEW fragment-extended compounds — this is the core action for fragment-based hit discovery / fragment screening. For targeted single-direction growing, use grow-from-atom.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds to grow from'),
    ],
    outputs: [
        param('results', 'object[]', 'Array of {compound, atom, bondVector, newCompound, errors} per combination'),
        // What a downstream step actually needs. `results` is one record per bond
        // vector tried, most of which produced no compound, so wiring it into a
        // CompoundList input hands the next action the wrong shape even when it
        // resolves.
        param('grownCompounds', T.CompoundList, 'The new compounds that growing produced'),
    ],
    parameters: [
        param('atomFilterFn', 'function', 'Filter for growable atoms', false),
        param('allowedVectors', 'object', 'Specific bond vectors per compound (Map)', false),
        param('maxSuggestions', 'number', 'Max suggestions per bond vector', false, { defaultValue: 10 }),
        param('fragments', 'string[]', 'Specific fragments to use', false),
        param('fragmentSets', 'string[]', 'Fragment sets to include', false),
        param('fragmentFn', 'function', 'Fragment eligibility filter', false),
        param('minimize', 'boolean', 'Energy minimize results after growing', false, { defaultValue: false }),
    ],
    dependsOn: [],
    preconditions: [
        pre('fragment_map_available', 'Fragment map must be available'),
        pre('compound_loaded', 'At least one compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'long',
    status: 'implemented',
    userAction: 'GrowFromAllAtoms',
    serverCommand: null,
    jobType: null,
};

// ─── Visibility / Lifecycle ────────────────────────────────────────────────

export const toggleFragmentVisibility = {
    id: 'toggle-fragment-visibility',
    name: 'Toggle Fragment Visibility',
    category: 'Fragment Operations',
    description: 'Show or hide a fragment in the 3D view. Loads fragment structure if not yet loaded.',
    inputs: [
        param('fragment', T.Fragment, 'Fragment to toggle'),
        param('forceValue', 'boolean', 'Force show/hide', false),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('fragment_data_available', 'Fragment must be available in library'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ToggleActiveFragment',
    serverCommand: null,
    jobType: null,
};

export const releaseFragmentMap = {
    id: 'release-fragment-map',
    name: 'Unload Fragment Library',
    category: 'Fragment Operations',
    description: 'Remove a loaded fragment library from the workspace to free memory.',
    inputs: [
        param('fragmentInfo', 'object', 'Fragment library identifier'),
    ],
    outputs: [],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('fragment_map_available', 'Fragment map must be loaded'),
    ],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: 'ReleaseFragmentMap',
    serverCommand: null,
    jobType: null,
};

// ─── Default export: all actions in this category ──────────────────────────

export default [
    loadFragmentMap,
    refreshAvailableFragments,
    loadFragments,
    growFromAtom,
    searchNear,
    confirmModification,
    replaceGroup,
    growToFragmentName,
    fragDataQuery,
    replaceAllGroups,
    growFromAllAtoms,
    toggleFragmentVisibility,
    releaseFragmentMap,
];
